import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import type {
  AdminAccess,
  AiAccess,
  OnboardingWelcomeContact,
  SubmitWelcomeFormInput,
  SupportTicketAccess,
  WelcomeContactSelection,
  WelcomeSectionKey,
} from '@catalyst-psa/shared';
import {
  ADMIN_ACCESS_LABELS,
  ADMIN_ACCESS_LEVELS,
  AI_ACCESS_LABELS,
  AI_ACCESS_LEVELS,
  SUPPORT_TICKET_ACCESS_LABELS,
  SUPPORT_TICKET_ACCESS_LEVELS,
  WELCOME_SECTION_KEYS,
  WELCOME_SECTION_LABELS,
} from '@catalyst-psa/shared';

import { useOnboardingWelcomeView, useSubmitWelcomeForm } from './api.js';

interface ContactForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  title: string;
}
interface AccessTriple {
  supportTicketAccess: SupportTicketAccess;
  aiAccess: AiAccess;
  adminAccess: AdminAccess;
}
interface SupportContactForm extends ContactForm, AccessTriple {}

const EMPTY_CONTACT: ContactForm = { firstName: '', lastName: '', email: '', phone: '', title: '' };
const DEFAULT_ACCESS: AccessTriple = {
  supportTicketAccess: 'no_access',
  aiAccess: 'no_access',
  adminAccess: 'no_access',
};
const EMPTY_SUPPORT: SupportContactForm = { ...EMPTY_CONTACT, ...DEFAULT_ACCESS };

const NEW_BILLING = '__new__';

/** Intro-page bullets, each tied to the section that provides it (hidden when disabled). */
const INTRO_BULLETS: [WelcomeSectionKey, string][] = [
  ['billing_address', 'Your billing address'],
  ['billing_contact', 'A billing contact (who receives invoices)'],
  ['billing_contact', "Anyone else who should be cc'd on invoices"],
  ['project_contacts', 'Anyone else who should be looped in on the project'],
  [
    'support_access',
    'People who you want to have access to create support tickets and access to our awesome AI tool Caia',
  ],
  ['weekly_reports', 'Who should get weekly automated updates on projects, tickets, and budget'],
  ['cat_plus', 'Who should get alerts on Catalyst+ hour consumption'],
  ['netsuite', 'Questions about NetSuite status, ready dates, and NetSuite system ID'],
];

/** Builds a contact selection for a new contact, or null when the row is empty. */
function newContactSelection(c: ContactForm): WelcomeContactSelection | null {
  if (!c.firstName.trim() && !c.email.trim()) return null;
  return {
    firstName: c.firstName.trim(),
    lastName: c.lastName.trim() || null,
    email: c.email.trim() || null,
    phone: c.phone.trim() || null,
    title: c.title.trim() || null,
  };
}

/** Display name for an existing contact. */
function contactName(c: OnboardingWelcomeContact): string {
  return `${c.firstName} ${c.lastName ?? ''}`.trim() || c.email || 'Contact';
}

// ---------------------------------------------------------------------------
// Small presentational inputs (self-contained; this page renders auth-free).
// ---------------------------------------------------------------------------

const INPUT_CLASS =
  'mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
}): JSX.Element {
  return (
    <label className="block text-sm">
      <span className="font-medium text-slate-700">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={INPUT_CLASS}
      />
    </label>
  );
}

function Select<T extends string>({
  label,
  value,
  options,
  labels,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly T[];
  labels: Record<T, string>;
  onChange: (v: T) => void;
}): JSX.Element {
  return (
    <label className="block text-sm">
      <span className="font-medium text-slate-700">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value as T)} className={INPUT_CLASS}>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {labels[opt]}
          </option>
        ))}
      </select>
    </label>
  );
}

function ContactFields({
  contact,
  onChange,
}: {
  contact: ContactForm;
  onChange: (next: ContactForm) => void;
}): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <Field
        label="First name"
        value={contact.firstName}
        onChange={(v) => onChange({ ...contact, firstName: v })}
      />
      <Field
        label="Last name"
        value={contact.lastName}
        onChange={(v) => onChange({ ...contact, lastName: v })}
      />
      <Field
        label="Email"
        type="email"
        value={contact.email}
        onChange={(v) => onChange({ ...contact, email: v })}
      />
      <Field
        label="Phone"
        value={contact.phone}
        onChange={(v) => onChange({ ...contact, phone: v })}
      />
      <div className="sm:col-span-2">
        <Field
          label="Title"
          value={contact.title}
          onChange={(v) => onChange({ ...contact, title: v })}
        />
      </div>
    </div>
  );
}

function AccessFields({
  access,
  onChange,
}: {
  access: AccessTriple;
  onChange: (next: AccessTriple) => void;
}): JSX.Element {
  return (
    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
      <Select
        label="Support Ticket Access"
        value={access.supportTicketAccess}
        options={SUPPORT_TICKET_ACCESS_LEVELS}
        labels={SUPPORT_TICKET_ACCESS_LABELS}
        onChange={(v) => onChange({ ...access, supportTicketAccess: v })}
      />
      <Select
        label="AI Access"
        value={access.aiAccess}
        options={AI_ACCESS_LEVELS}
        labels={AI_ACCESS_LABELS}
        onChange={(v) => onChange({ ...access, aiAccess: v })}
      />
      <Select
        label="Admin Access"
        value={access.adminAccess}
        options={ADMIN_ACCESS_LEVELS}
        labels={ADMIN_ACCESS_LABELS}
        onChange={(v) => onChange({ ...access, adminAccess: v })}
      />
    </div>
  );
}

export function PublicWelcomePage(): JSX.Element {
  const { token = '' } = useParams<{ token: string }>();
  const viewQuery = useOnboardingWelcomeView(token);
  const submitMutation = useSubmitWelcomeForm(token);

  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);

  // Billing address.
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [stateField, setStateField] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [phone, setPhone] = useState('');
  // Billing contact.
  const [billingChoice, setBillingChoice] = useState<string>(NEW_BILLING);
  const [billingNew, setBillingNew] = useState<ContactForm>(EMPTY_CONTACT);
  const [billingCcEmails, setBillingCcEmails] = useState('');
  // Project contacts.
  const [projectSelected, setProjectSelected] = useState<Record<string, boolean>>({});
  const [projectNew, setProjectNew] = useState<ContactForm[]>([]);
  // Support contacts.
  const [supportSelected, setSupportSelected] = useState<Record<string, boolean>>({});
  const [supportAccess, setSupportAccess] = useState<Record<string, AccessTriple>>({});
  const [supportNew, setSupportNew] = useState<SupportContactForm[]>([]);
  // Weekly report recipients.
  const [weeklySelected, setWeeklySelected] = useState<Record<string, boolean>>({});
  const [weeklyNew, setWeeklyNew] = useState<ContactForm[]>([]);
  // Catalyst+ usage alerts.
  const [catPlusSelected, setCatPlusSelected] = useState<Record<string, boolean>>({});
  const [catPlusNew, setCatPlusNew] = useState<ContactForm[]>([]);
  // Existing contacts the customer marked as no longer with the company.
  const [inactiveMarked, setInactiveMarked] = useState<Record<string, boolean>>({});
  // NetSuite.
  const [netsuiteAccountId, setNetsuiteAccountId] = useState('');
  const [netsuiteStatus, setNetsuiteStatus] = useState('');
  const [goLiveDate, setGoLiveDate] = useState('');

  const prefilled = useRef(false);
  const view = viewQuery.data;
  const existingContacts = view?.contacts ?? [];

  // Enabled form sections (in display order) — disabled sections are removed entirely and
  // the remaining ones renumber. Fallback to every section when none are reported.
  const sections: WelcomeSectionKey[] =
    view && view.sections.length > 0 ? view.sections : [...WELCOME_SECTION_KEYS];
  const sectionEnabled = (key: WelcomeSectionKey): boolean => sections.includes(key);
  const current: WelcomeSectionKey = sections[Math.min(step, sections.length - 1)] ?? 'welcome';

  useEffect(() => {
    if (!view || prefilled.current) return;
    prefilled.current = true;
    setAddressLine1(view.addressLine1 ?? '');
    setAddressLine2(view.addressLine2 ?? '');
    setCity(view.city ?? '');
    setStateField(view.state ?? '');
    setPostalCode(view.postalCode ?? '');
    setPhone(view.phone ?? '');
    setBillingCcEmails(view.billingCcEmails ?? '');
    setNetsuiteAccountId(view.netsuiteAccountId ?? '');
    setNetsuiteStatus(view.netsuiteStatus ?? '');
    setGoLiveDate(view.goLiveDate ?? '');

    // Billing: preselect the current billing contact if there is one.
    const billing = view.contacts.find((c) => c.billingContact);
    setBillingChoice(
      billing ? billing.id : view.contacts.length > 0 ? view.contacts[0]!.id : NEW_BILLING,
    );

    // Project: precheck existing project contacts.
    const projSel: Record<string, boolean> = {};
    for (const c of view.contacts) if (c.projectContact) projSel[c.id] = true;
    setProjectSelected(projSel);

    // Support: precheck existing support contacts + seed access from current values.
    const supSel: Record<string, boolean> = {};
    const supAcc: Record<string, AccessTriple> = {};
    for (const c of view.contacts) {
      supAcc[c.id] = {
        supportTicketAccess: c.supportTicketAccess,
        aiAccess: c.aiAccess,
        adminAccess: c.adminAccess,
      };
      if (c.supportContact) supSel[c.id] = true;
    }
    setSupportSelected(supSel);
    setSupportAccess(supAcc);

    // Weekly report recipients: precheck contacts already enrolled.
    const weeklySel: Record<string, boolean> = {};
    for (const c of view.contacts) if (c.weeklyReportRecipient) weeklySel[c.id] = true;
    setWeeklySelected(weeklySel);

    // Catalyst+ alerts: precheck contacts already flagged.
    const catSel: Record<string, boolean> = {};
    for (const c of view.contacts) if (c.catPlusNotifications) catSel[c.id] = true;
    setCatPlusSelected(catSel);
    // eslint config has no exhaustive-deps rule.
  }, [view]);

  /** Marks a contact inactive: unselects it everywhere so it can't also be flagged. */
  const markInactive = (id: string): void => {
    setInactiveMarked((s) => ({ ...s, [id]: true }));
    setProjectSelected((s) => ({ ...s, [id]: false }));
    setSupportSelected((s) => ({ ...s, [id]: false }));
    setWeeklySelected((s) => ({ ...s, [id]: false }));
    setCatPlusSelected((s) => ({ ...s, [id]: false }));
    setBillingChoice((choice) => (choice === id ? NEW_BILLING : choice));
  };
  const undoInactive = (id: string): void => {
    setInactiveMarked((s) => ({ ...s, [id]: false }));
  };

  const handleSubmit = (): void => {
    const billingContact: WelcomeContactSelection | null =
      billingChoice === NEW_BILLING
        ? newContactSelection(billingNew)
        : { contactId: billingChoice };

    const projectContacts: WelcomeContactSelection[] = [
      ...existingContacts.filter((c) => projectSelected[c.id]).map((c) => ({ contactId: c.id })),
      ...projectNew.flatMap((c) => {
        const v = newContactSelection(c);
        return v ? [v] : [];
      }),
    ];

    const supportContacts = [
      ...existingContacts
        .filter((c) => supportSelected[c.id])
        .map((c) => ({ contactId: c.id, ...(supportAccess[c.id] ?? DEFAULT_ACCESS) })),
      ...supportNew.flatMap((c) => {
        const base = newContactSelection(c);
        return base
          ? [
              {
                ...base,
                supportTicketAccess: c.supportTicketAccess,
                aiAccess: c.aiAccess,
                adminAccess: c.adminAccess,
              },
            ]
          : [];
      }),
    ];

    const weeklyReportContacts: WelcomeContactSelection[] = [
      ...existingContacts.filter((c) => weeklySelected[c.id]).map((c) => ({ contactId: c.id })),
      ...weeklyNew.flatMap((c) => {
        const v = newContactSelection(c);
        return v ? [v] : [];
      }),
    ];

    const catPlusContacts: WelcomeContactSelection[] = [
      ...existingContacts.filter((c) => catPlusSelected[c.id]).map((c) => ({ contactId: c.id })),
      ...catPlusNew.flatMap((c) => {
        const v = newContactSelection(c);
        return v ? [v] : [];
      }),
    ];

    const inactiveContactIds = existingContacts
      .filter((c) => inactiveMarked[c.id])
      .map((c) => c.id);

    // Only submit data for the sections that were actually shown on the form.
    const input: SubmitWelcomeFormInput = {
      ...(inactiveContactIds.length > 0 ? { inactiveContactIds } : {}),
      ...(sectionEnabled('billing_address')
        ? {
            addressLine1: addressLine1.trim() || null,
            addressLine2: addressLine2.trim() || null,
            city: city.trim() || null,
            state: stateField.trim() || null,
            postalCode: postalCode.trim() || null,
            phone: phone.trim() || null,
          }
        : {}),
      ...(sectionEnabled('billing_contact')
        ? { billingContact, billingCcEmails: billingCcEmails.trim() || null }
        : {}),
      ...(sectionEnabled('project_contacts') ? { projectContacts } : {}),
      ...(sectionEnabled('support_access') ? { supportContacts } : {}),
      ...(sectionEnabled('weekly_reports') ? { weeklyReportContacts } : {}),
      ...(sectionEnabled('cat_plus') ? { catPlusContacts } : {}),
      ...(sectionEnabled('netsuite')
        ? {
            netsuiteAccountId: netsuiteAccountId.trim() || null,
            netsuiteStatus: netsuiteStatus.trim() || null,
            goLiveDate: goLiveDate || null,
          }
        : {}),
    };
    submitMutation.mutate(input, { onSuccess: () => setDone(true) });
  };

  if (viewQuery.isLoading) {
    return (
      <Shell>
        <p className="text-center text-sm text-slate-500">Loading…</p>
      </Shell>
    );
  }

  if (viewQuery.isError || !view) {
    return (
      <Shell>
        <div className="text-center">
          <h1 className="text-lg font-semibold text-slate-900">Welcome link unavailable</h1>
          <p className="mt-2 text-sm text-slate-500">
            This link is invalid or has been disabled. Please contact your Catalyst representative.
          </p>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="h-6 w-6"
            >
              <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h1 className="mt-3 text-lg font-semibold text-slate-900">Thank you!</h1>
          <p className="mt-2 text-sm text-slate-500">
            Your information has been submitted to Catalyst. You can close this window.
          </p>
        </div>
      </Shell>
    );
  }

  const isLast = step === sections.length - 1;
  const error =
    submitMutation.isError && submitMutation.error instanceof Error
      ? submitMutation.error.message
      : null;

  // Names that will be flagged, for the "before moving on" summaries.
  const projectFlagged = [
    ...existingContacts.filter((c) => projectSelected[c.id]).map(contactName),
    ...projectNew
      .filter((c) => c.firstName.trim() || c.email.trim())
      .map((c) => c.firstName.trim() || c.email.trim()),
  ];
  const supportFlagged = [
    ...existingContacts.filter((c) => supportSelected[c.id]).map(contactName),
    ...supportNew
      .filter((c) => c.firstName.trim() || c.email.trim())
      .map((c) => c.firstName.trim() || c.email.trim()),
  ];
  const weeklyFlagged = [
    ...existingContacts.filter((c) => weeklySelected[c.id]).map(contactName),
    ...weeklyNew
      .filter((c) => c.firstName.trim() || c.email.trim())
      .map((c) => c.firstName.trim() || c.email.trim()),
  ];
  const catPlusFlagged = [
    ...existingContacts.filter((c) => catPlusSelected[c.id]).map(contactName),
    ...catPlusNew
      .filter((c) => c.firstName.trim() || c.email.trim())
      .map((c) => c.firstName.trim() || c.email.trim()),
  ];

  return (
    <Shell wide>
      <div className="mb-1 text-xs font-medium uppercase tracking-wide text-brand-600">
        Catalyst onboarding
      </div>
      <h1 className="text-xl font-semibold text-slate-900">Welcome, {view.customerName}</h1>
      <p className="mt-1 text-sm text-slate-500">
        Please confirm a few details so we can get your account set up.
      </p>
      {view.submitted ? (
        <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">
          This form was already submitted. You can review and resubmit to update your information.
        </p>
      ) : null}

      {/* Step indicator (only enabled sections, renumbered) */}
      <ol className="mt-5 flex flex-wrap gap-2">
        {sections.map((key, i) => (
          <li
            key={key}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
              i === step
                ? 'bg-brand-600 text-white'
                : i < step
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-slate-100 text-slate-500'
            }`}
          >
            <span>{i + 1}</span>
            <span className="hidden sm:inline">{WELCOME_SECTION_LABELS[key]}</span>
          </li>
        ))}
      </ol>

      <div className="mt-6 min-h-[16rem]">
        {current === 'welcome' ? (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-900">Hi {view.customerName} 👋</h2>
            <p className="text-sm text-slate-600">
              We&apos;re thrilled to have you on board. This quick form helps us set up your
              account so we can start delivering value right away.
            </p>
            <div>
              <p className="text-sm font-medium text-slate-700">You&apos;ll provide:</p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-600">
                {INTRO_BULLETS.filter(([key]) => sectionEnabled(key)).map(([, text], i) => (
                  <li key={i}>{text}</li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}

        {current === 'billing_address' ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Please fill in your billing address below and select next.
            </p>
            <Field label="Address line 1" value={addressLine1} onChange={setAddressLine1} />
            <Field label="Address line 2" value={addressLine2} onChange={setAddressLine2} />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Field label="City" value={city} onChange={setCity} />
              <Field label="State" value={stateField} onChange={setStateField} />
              <Field label="Postal code" value={postalCode} onChange={setPostalCode} />
            </div>
            <Field label="Phone" value={phone} onChange={setPhone} />
          </div>
        ) : null}

        {current === 'billing_contact' ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Who should receive invoices? Choose an existing contact or add a new one.
            </p>
            <div className="space-y-2">
              {existingContacts.map((c) => {
                const marked = Boolean(inactiveMarked[c.id]);
                return (
                  <label
                    key={c.id}
                    className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 text-sm ${
                      marked
                        ? 'border-slate-200 bg-slate-50 opacity-70'
                        : billingChoice === c.id
                          ? 'border-brand-500 bg-brand-50'
                          : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="billing"
                      disabled={marked}
                      checked={billingChoice === c.id}
                      onChange={() => setBillingChoice(c.id)}
                      className="h-4 w-4 text-brand-600 focus:ring-brand-500"
                    />
                    <span className={marked ? 'line-through' : undefined}>
                      <span className="font-medium text-slate-800">{contactName(c)}</span>
                      {c.email ? <span className="text-slate-500"> · {c.email}</span> : null}
                      {marked ? (
                        <InactiveBadge />
                      ) : c.billingContact ? (
                        <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700">
                          Current billing contact
                        </span>
                      ) : null}
                    </span>
                    <InactiveToggle
                      marked={marked}
                      onMark={() => markInactive(c.id)}
                      onUndo={() => undoInactive(c.id)}
                    />
                  </label>
                );
              })}
              <label
                className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 text-sm ${
                  billingChoice === NEW_BILLING
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                <input
                  type="radio"
                  name="billing"
                  checked={billingChoice === NEW_BILLING}
                  onChange={() => setBillingChoice(NEW_BILLING)}
                  className="h-4 w-4 text-brand-600 focus:ring-brand-500"
                />
                <span className="font-medium text-slate-800">Add a new billing contact</span>
              </label>
            </div>
            {billingChoice === NEW_BILLING ? (
              <div className="rounded-lg border border-slate-200 p-4">
                <ContactFields contact={billingNew} onChange={setBillingNew} />
              </div>
            ) : null}
            <label className="block text-sm">
              <span className="font-medium text-slate-700">Additional billing CC emails</span>
              <textarea
                rows={2}
                value={billingCcEmails}
                placeholder="Separate multiple emails with commas"
                onChange={(e) => setBillingCcEmails(e.target.value)}
                className={INPUT_CLASS}
              />
            </label>
          </div>
        ) : null}

        {current === 'project_contacts' ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Select the people we&apos;ll work with on project delivery. You can also add new
              contacts.
            </p>
            {existingContacts.length > 0 ? (
              <div className="space-y-2">
                {existingContacts.map((c) => {
                  const marked = Boolean(inactiveMarked[c.id]);
                  return (
                    <label
                      key={c.id}
                      className={`flex cursor-pointer items-center gap-3 rounded-lg border border-slate-200 p-3 text-sm ${
                        marked ? 'bg-slate-50 opacity-70' : 'hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        disabled={marked}
                        checked={Boolean(projectSelected[c.id])}
                        onChange={(e) =>
                          setProjectSelected((s) => ({ ...s, [c.id]: e.target.checked }))
                        }
                        className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                      />
                      <span className={marked ? 'line-through' : undefined}>
                        <span className="font-medium text-slate-800">{contactName(c)}</span>
                        {c.email ? <span className="text-slate-500"> · {c.email}</span> : null}
                        {marked ? <InactiveBadge /> : null}
                      </span>
                      <InactiveToggle
                        marked={marked}
                        onMark={() => markInactive(c.id)}
                        onUndo={() => undoInactive(c.id)}
                      />
                    </label>
                  );
                })}
              </div>
            ) : null}

            <NewContactRows
              contacts={projectNew}
              onChange={setProjectNew}
              empty={EMPTY_CONTACT}
              addLabel="Add a new project contact"
            />

            <FlaggedSummary label="project contacts" names={projectFlagged} />
          </div>
        ) : null}

        {current === 'support_access' ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Who should be able to create support tickets, see dashboards, and use our AI tool
              Caia? Select the people below and set their access levels. You can also add new
              contacts.
            </p>
            {existingContacts.length > 0 ? (
              <div className="space-y-2">
                {existingContacts.map((c) => {
                  const checked = Boolean(supportSelected[c.id]);
                  const marked = Boolean(inactiveMarked[c.id]);
                  const access = supportAccess[c.id] ?? DEFAULT_ACCESS;
                  return (
                    <div
                      key={c.id}
                      className={`rounded-lg border border-slate-200 p-3 ${
                        marked ? 'bg-slate-50 opacity-70' : ''
                      }`}
                    >
                      <label className="flex cursor-pointer items-center gap-3 text-sm">
                        <input
                          type="checkbox"
                          disabled={marked}
                          checked={checked}
                          onChange={(e) =>
                            setSupportSelected((s) => ({ ...s, [c.id]: e.target.checked }))
                          }
                          className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                        />
                        <span className={marked ? 'line-through' : undefined}>
                          <span className="font-medium text-slate-800">{contactName(c)}</span>
                          {c.email ? <span className="text-slate-500"> · {c.email}</span> : null}
                          {marked ? <InactiveBadge /> : null}
                        </span>
                        <InactiveToggle
                          marked={marked}
                          onMark={() => markInactive(c.id)}
                          onUndo={() => undoInactive(c.id)}
                        />
                      </label>
                      {checked && !marked ? (
                        <AccessFields
                          access={access}
                          onChange={(next) => setSupportAccess((s) => ({ ...s, [c.id]: next }))}
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}

            <div className="space-y-4">
              {supportNew.map((contact, index) => (
                <div key={index} className="rounded-lg border border-slate-200 p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-400">
                      New support contact {index + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => setSupportNew((rows) => rows.filter((_, i) => i !== index))}
                      className="text-xs font-medium text-rose-600 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                  <ContactFields
                    contact={contact}
                    onChange={(next) =>
                      setSupportNew((rows) =>
                        rows.map((r, i) => (i === index ? { ...r, ...next } : r)),
                      )
                    }
                  />
                  <AccessFields
                    access={contact}
                    onChange={(next) =>
                      setSupportNew((rows) =>
                        rows.map((r, i) => (i === index ? { ...r, ...next } : r)),
                      )
                    }
                  />
                </div>
              ))}
              <button
                type="button"
                onClick={() => setSupportNew((rows) => [...rows, { ...EMPTY_SUPPORT }])}
                className="rounded-md border border-dashed border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                + Add a new support contact
              </button>
            </div>

            <FlaggedSummary label="support contacts" names={supportFlagged} />
          </div>
        ) : null}

        {current === 'weekly_reports' ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Who should get weekly automated updates? This email will include any open projects,
              tickets, hours used against budget and Catalyst+ usage if you are a Catalyst+ member.
              Pick from your existing contacts or add new ones.
            </p>
            {existingContacts.length > 0 ? (
              <div className="space-y-2">
                {existingContacts.map((c) => {
                  const marked = Boolean(inactiveMarked[c.id]);
                  return (
                    <label
                      key={c.id}
                      className={`flex cursor-pointer items-center gap-3 rounded-lg border border-slate-200 p-3 text-sm ${
                        marked ? 'bg-slate-50 opacity-70' : 'hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        disabled={marked}
                        checked={Boolean(weeklySelected[c.id])}
                        onChange={(e) =>
                          setWeeklySelected((s) => ({ ...s, [c.id]: e.target.checked }))
                        }
                        className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                      />
                      <span className={marked ? 'line-through' : undefined}>
                        <span className="font-medium text-slate-800">{contactName(c)}</span>
                        {c.email ? <span className="text-slate-500"> · {c.email}</span> : null}
                        {marked ? <InactiveBadge /> : null}
                      </span>
                      <InactiveToggle
                        marked={marked}
                        onMark={() => markInactive(c.id)}
                        onUndo={() => undoInactive(c.id)}
                      />
                    </label>
                  );
                })}
              </div>
            ) : null}

            <NewContactRows
              contacts={weeklyNew}
              onChange={setWeeklyNew}
              empty={EMPTY_CONTACT}
              addLabel="Add a new weekly report recipient"
            />

            <FlaggedSummary label="weekly report recipients" names={weeklyFlagged} />
          </div>
        ) : null}

        {current === 'cat_plus' ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Who should get alerts on Catalyst+ hour consumption? Pick from your existing contacts
              or add new ones.
            </p>
            {existingContacts.length > 0 ? (
              <div className="space-y-2">
                {existingContacts.map((c) => {
                  const marked = Boolean(inactiveMarked[c.id]);
                  return (
                    <label
                      key={c.id}
                      className={`flex cursor-pointer items-center gap-3 rounded-lg border border-slate-200 p-3 text-sm ${
                        marked ? 'bg-slate-50 opacity-70' : 'hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        disabled={marked}
                        checked={Boolean(catPlusSelected[c.id])}
                        onChange={(e) =>
                          setCatPlusSelected((s) => ({ ...s, [c.id]: e.target.checked }))
                        }
                        className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                      />
                      <span className={marked ? 'line-through' : undefined}>
                        <span className="font-medium text-slate-800">{contactName(c)}</span>
                        {c.email ? <span className="text-slate-500"> · {c.email}</span> : null}
                        {marked ? <InactiveBadge /> : null}
                      </span>
                      <InactiveToggle
                        marked={marked}
                        onMark={() => markInactive(c.id)}
                        onUndo={() => undoInactive(c.id)}
                      />
                    </label>
                  );
                })}
              </div>
            ) : null}

            <NewContactRows
              contacts={catPlusNew}
              onChange={setCatPlusNew}
              empty={EMPTY_CONTACT}
              addLabel="Add a new Catalyst+ alert recipient"
            />

            <FlaggedSummary label="Catalyst+ alert recipients" names={catPlusFlagged} />
          </div>
        ) : null}

        {current === 'netsuite' ? (
          <div className="space-y-3">
            <Field
              label="NetSuite Account ID"
              value={netsuiteAccountId}
              onChange={setNetsuiteAccountId}
            />
            <Field label="NetSuite status" value={netsuiteStatus} onChange={setNetsuiteStatus} />
            <Field label="Go-live date" type="date" value={goLiveDate} onChange={setGoLiveDate} />
          </div>
        ) : null}
      </div>

      {error ? (
        <p className="mt-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
      ) : null}

      {current === 'welcome' && !isLast ? (
        <div className="mt-6 flex justify-center border-t border-slate-100 pt-5">
          <button
            type="button"
            onClick={() => setStep((s) => Math.min(sections.length - 1, s + 1))}
            className="rounded-md bg-brand-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Let&apos;s get started
          </button>
        </div>
      ) : (
        <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4">
          <button
            type="button"
            disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            className="rounded-md px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-40"
          >
            Back
          </button>
          {isLast ? (
            <button
              type="button"
              disabled={submitMutation.isPending}
              onClick={handleSubmit}
              className="rounded-md bg-brand-600 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
            >
              {submitMutation.isPending ? 'Submitting…' : 'Submit'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setStep((s) => Math.min(sections.length - 1, s + 1))}
              className="rounded-md bg-brand-600 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-700"
            >
              Next
            </button>
          )}
        </div>
      )}
    </Shell>
  );
}

function NewContactRows({
  contacts,
  onChange,
  empty,
  addLabel,
}: {
  contacts: ContactForm[];
  onChange: (next: ContactForm[]) => void;
  empty: ContactForm;
  addLabel: string;
}): JSX.Element {
  return (
    <div className="space-y-4">
      {contacts.map((contact, index) => (
        <div key={index} className="rounded-lg border border-slate-200 p-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-400">
              New contact {index + 1}
            </span>
            <button
              type="button"
              onClick={() => onChange(contacts.filter((_, i) => i !== index))}
              className="text-xs font-medium text-rose-600 hover:underline"
            >
              Remove
            </button>
          </div>
          <ContactFields
            contact={contact}
            onChange={(next) => onChange(contacts.map((c, i) => (i === index ? next : c)))}
          />
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...contacts, { ...empty }])}
        className="rounded-md border border-dashed border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
      >
        + {addLabel}
      </button>
    </div>
  );
}

function FlaggedSummary({ label, names }: { label: string; names: string[] }): JSX.Element {
  return (
    <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
      {names.length > 0 ? (
        <>
          Will be flagged as {label}: <span className="font-medium">{names.join(', ')}</span>
        </>
      ) : (
        <>No {label} selected yet.</>
      )}
    </div>
  );
}

/** "Mark inactive" / "Undo" toggle shown next to each existing contact. */
function InactiveToggle({
  marked,
  onMark,
  onUndo,
}: {
  marked: boolean;
  onMark: () => void;
  onUndo: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      title="Mark this person as no longer with your company"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (marked) onUndo();
        else onMark();
      }}
      className={`ml-auto shrink-0 text-xs font-medium hover:underline ${
        marked ? 'text-slate-500' : 'text-rose-600'
      }`}
    >
      {marked ? 'Undo' : 'Mark inactive'}
    </button>
  );
}

function InactiveBadge(): JSX.Element {
  return (
    <span className="ml-2 rounded-full bg-rose-100 px-2 py-0.5 text-xs text-rose-700">
      Will be marked inactive
    </span>
  );
}

function Shell({ children, wide }: { children: React.ReactNode; wide?: boolean }): JSX.Element {
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div
        className={`mx-auto rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8 ${
          wide ? 'max-w-2xl' : 'max-w-md'
        }`}
      >
        {children}
      </div>
    </div>
  );
}
